import { MachineImportsController_list_v1, type MachineImportSummary } from '@repo/api-client';
import { Button } from '@repo/ui/components/button';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui/components/table';
import { IconUpload } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';

import { useApiClient } from '@/api/api-client';
import { isForbidden } from '@/api/failure';
import { formatInstant } from '@/lib/instant';

import { currentCursor, type CursorTrail, FIRST_PAGE } from '../back-office/cursor-pagination';
import { CursorPager } from '../back-office/CursorPager';
import {
  EmptyState,
  LoadFailed,
  Screen,
  ScreenTable,
  TableNotice,
  TableSkeleton,
} from '../back-office/Screen';
import { machineImportPath, paths } from '../paths';

/**
 * Every billing machine file uploaded: `GET /v1/machine-imports`, newest first.
 * Grows with use, so keyset paged with `CursorPager` — never an offset.
 */

type ListState =
  | { readonly kind: 'loading' }
  | {
      readonly kind: 'loaded';
      readonly items: readonly MachineImportSummary[];
      readonly nextCursor: string | null;
    }
  | { readonly kind: 'failed'; readonly error: unknown };

const COLUMNS = 6;

export function MachineUploads() {
  const client = useApiClient();

  const [trail, setTrail] = useState<CursorTrail>(FIRST_PAGE);
  const [state, setState] = useState<ListState>({ kind: 'loading' });
  const [reloads, setReloads] = useState(0);

  const cursor = currentCursor(trail);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    setState({ kind: 'loading' });

    client
      .request(MachineImportsController_list_v1, {
        query: { cursor: cursor ?? undefined },
        signal: controller.signal,
      })
      .then((answer) => {
        if (live) setState({ kind: 'loaded', items: answer.items, nextCursor: answer.nextCursor });
      })
      .catch((error: unknown) => {
        if (live && !(error instanceof DOMException && error.name === 'AbortError')) {
          setState({ kind: 'failed', error });
        }
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, cursor, reloads]);

  const items = state.kind === 'loaded' ? state.items : [];

  return (
    <Screen
      title="Machine uploads"
      description="Every file uploaded from the ride and activity billing machines, newest first, with what each did. The file itself is kept exactly as it was uploaded and can be downloaded from its record."
      action={
        <Button nativeButton={false} render={<Link to={paths.machineUpload} />}>
          <IconUpload data-icon="inline-start" />
          Upload a file
        </Button>
      }
    >
      <ScreenTable label="Machine uploads" columns="stated">
        <TableHeader>
          <TableRow>
            <TableHead className="w-52">Uploaded</TableHead>
            <TableHead className="w-56">File</TableHead>
            <TableHead className="w-40">By</TableHead>
            <TableHead className="w-24 text-right">Created</TableHead>
            <TableHead className="w-36 text-right">Already imported</TableHead>
            <TableHead className="w-24 text-right">Refused</TableHead>
          </TableRow>
        </TableHeader>

        {state.kind === 'loading' && <TableSkeleton columns={COLUMNS} />}

        {state.kind === 'failed' && (
          <TableNotice columns={COLUMNS}>
            {isForbidden(state.error) ? (
              <EmptyState
                title="This account cannot read the machine uploads"
                description="Machine uploads are the owner's and the manager's. If that is what you are, sign out and back in — the role this browser is holding may be out of date."
              />
            ) : (
              <LoadFailed
                error={state.error}
                onRetry={() => {
                  setReloads((count) => count + 1);
                }}
              />
            )}
          </TableNotice>
        )}

        {state.kind === 'loaded' &&
          (items.length === 0 ? (
            <TableNotice columns={COLUMNS}>
              <EmptyState
                title="No machine files uploaded yet"
                description="Export ORDERS.CSV from a billing machine and upload it. You see what it would do before anything is written."
              />
            </TableNotice>
          ) : (
            <TableBody>
              {items.map((upload) => (
                <UploadRow key={upload.id ?? upload.fileSha256} upload={upload} />
              ))}
            </TableBody>
          ))}
      </ScreenTable>

      {state.kind === 'loaded' && (
        <CursorPager
          trail={trail}
          rows={items.length}
          nextCursor={state.nextCursor}
          onTrailChange={setTrail}
          label="machine uploads"
          noun="upload"
        />
      )}
    </Screen>
  );
}

function UploadRow({ upload }: { upload: MachineImportSummary }) {
  const { created, alreadyImported, refused } = upload.counts;

  return (
    <TableRow>
      <TableCell className="numeric">
        {upload.recordedAt === null ? '' : formatInstant(upload.recordedAt)}
      </TableCell>
      <TableCell className="whitespace-normal">
        {upload.id === null ? (
          <span className="font-mono">{upload.fileName}</span>
        ) : (
          <Link
            to={machineImportPath(upload.id)}
            aria-label={`The record for the upload of ${upload.fileName}`}
            className="rounded-sm font-mono break-all text-foreground underline decoration-border decoration-dotted underline-offset-4 outline-none hover:decoration-current focus-visible:ring-3 focus-visible:ring-ring/30"
          >
            {upload.fileName}
          </Link>
        )}
      </TableCell>
      <TableCell>{upload.uploadedBy?.displayName ?? ''}</TableCell>
      <TableCell className="text-right font-mono numeric">{created}</TableCell>
      <TableCell className="text-right font-mono text-muted-foreground numeric">
        {alreadyImported}
      </TableCell>
      <TableCell
        className={`text-right font-mono numeric ${refused > 0 ? 'text-destructive' : 'text-muted-foreground'}`}
      >
        {refused}
      </TableCell>
    </TableRow>
  );
}
